/**
 * src/components/notes/NotesTab.jsx — Collaborative Notes timeline (orchestrator).
 *
 * Generalized from src/components/projects/HistoryTab.jsx. Scope-agnostic: drives
 * the project Notes tab, the org-wide (general) notes view, and the bundle Notes
 * section off ONE component.
 *
 * Design pass (2026-06-08, acn-frontend-design): "craft over chrome, maritime
 * restraint" — bolder cards via subtle depth + a 3px accent rail on decisions
 * (not loud colour); a pinned DECISIONS section on top; the rest day-grouped
 * (Today / Yesterday / dated) with hairline rules.
 *
 * Threads (2026-06-08): notes are lightweight threads. Roots (parent_id NULL)
 * keep the pinned + day-grouped layout; each root's replies render INDENTED
 * beneath it via NoteThread. The AUTHOR edits/re-tags their own entries; ANY
 * authenticated user replies. This file is the orchestrator (load, grouping,
 * thread-tree assembly); NoteThread + NoteCard own the per-entry rendering.
 *
 * Props:
 *   scope   — 'project' | 'bundle' | 'general'
 *   scopeId — the project_id / bundle_id (omit for 'general')
 *
 * Contract (src/lib/api/notes.js): listNotes / createNote / archiveNote / updateNote.
 * Mobile-first, 44px touch targets, ACN design tokens (no raw hex), i18n via t().
 */

import { useEffect, useState, useCallback, useMemo, useContext } from 'react';
import { StickyNote, Plus, Star } from 'lucide-react';
import { listNotes, createNote, archiveNote, updateNote } from '../../lib/api/notes';
import { useT } from '../../lib/i18n/I18nContext';
import { useContentLanguage } from '../../lib/i18n/useContentLanguage';
import { useLocalizedRows } from '../../lib/i18n/useLocalizedRows';
import ContentLangToggle from '../shared/ContentLangToggle';
import AuthContext from '../../auth/AuthContext';
import NoteThread from './NoteThread';
import { editContent } from '../../lib/api/translations';
import { shownLanguage } from '../../../lib/quote-doc/translations.mjs';

// Composer note_type chips. note_type column defaults to 'note' server-side.
const NOTE_TYPES = [
  { key: 'note', labelKey: 'notes.typeNote' },
  { key: 'decision', labelKey: 'notes.typeDecision' },
  { key: 'risk', labelKey: 'notes.typeRisk' },
  { key: 'followup', labelKey: 'notes.typeFollowup' },
  // TICKET-735 (CEO 2026-09-03): what is missing + dated reminders (due_at → Focus).
  { key: 'missing', labelKey: 'notes.typeMissing' },
  { key: 'reminder', labelKey: 'notes.typeReminder' },
];

// Soft maritime depth — rgba shadow is the sanctioned exception to the no-hex
// rule (overlays/shadows). Subtle, not a drop-shadow.
const CARD_SHADOW = '0 1px 2px rgba(0,0,0,0.05), 0 4px 14px rgba(0,0,0,0.04)';

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x.getTime(); };

/** A spoken/typed day bucket label: Today / Yesterday / a short dated header. */
function dayLabel(iso, t, locale) {
  const created = new Date(iso || 0);
  const today = startOfDay(new Date());
  const that = startOfDay(created);
  const ONE_DAY = 86400000;
  if (that === today) return t('notes.today');
  if (that === today - ONE_DAY) return t('notes.yesterday');
  try {
    return created.toLocaleDateString(locale || undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  } catch {
    return created.toLocaleDateString();
  }
}

/** Section header — uppercase label + hairline rule + count. Accent variant for pinned. */
function GroupHeader({ label, count, accent }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 2px 10px' }}>
      {accent && <Star size={12} color="var(--accent)" />}
      <span style={{
        fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase',
        color: accent ? 'var(--accent)' : 'var(--tm)',
      }}>
        {label}
      </span>
      <span style={{ height: 1, flex: 1, background: 'var(--bdrg)' }} />
      <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--tm)' }}>{count}</span>
    </div>
  );
}

// TICKET-735: scope 'task' (scopeId = task_id; projectId optional, denormalised).
export default function NotesTab({ scope = 'project', scopeId, projectId: projectIdProp }) {
  const { t, locale } = useT();
  const { lang } = useContentLanguage();
  // TICKET-735: NotesTab now mounts inside TaskDetailPanel, which renders in
  // contexts/tests without an AuthProvider. Degrade to no-role (read-only chrome)
  // instead of throwing — the null-safe context read PhotoGallery uses
  // (PhotoGallery.jsx:118), NOT the throwing useAuth() hook.
  //
  // A try/catch around useAuth() looked equivalent and is not: if it throws,
  // every hook after it is skipped for that render and React's hook order
  // breaks. react-hooks/rules-of-hooks flags it for exactly that reason.
  // useContext never throws, so the hook call stays unconditional.
  const auth = useContext(AuthContext);
  const role = auth?.role;
  const profile = auth?.profile ?? null;
  const canArchive = !!role; // any authenticated user (collaborative feature)
  const currentUserId = profile?.id || null;
  // !! guard: an unknown current user id => no edit affordance anywhere.
  const canEdit = useCallback(
    (note) => !!currentUserId && note?.author_id === currentUserId,
    [currentUserId],
  );

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [body, setBody] = useState('');
  const [noteType, setNoteType] = useState('note');
  const [dueAt, setDueAt] = useState(''); // TICKET-735 reminder date (yyyy-mm-dd)
  const [saving, setSaving] = useState(false);

  const projectId = scope === 'project' ? scopeId : (scope === 'task' ? projectIdProp : undefined);
  const bundleId = scope === 'bundle' ? scopeId : undefined;
  const taskId = scope === 'task' ? scopeId : undefined;

  // NOTE: `t` is intentionally NOT in these dependency arrays. The real useT()
  // memoizes `t` per-locale, but test/SSR fallbacks return a fresh `t` each
  // render — including it would re-fire the load effect every render (infinite
  // loading loop). Error strings read `t` at call time, which is correct.
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listNotes({ scope, projectId, bundleId, taskId });
      setNotes(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || t('notes.loadError'));
      setNotes([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t excluded by design (see note above)
  }, [scope, projectId, bundleId, taskId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = useCallback(async () => {
    const trimmed = body.trim();
    if (trimmed.length === 0 || saving) return;
    setSaving(true);
    setError(null);
    try {
      await createNote({
        scope,
        projectId,
        bundleId,
        taskId,
        body: trimmed,
        noteType,
        isDecision: noteType === 'decision',
        dueAt: noteType === 'reminder' && dueAt ? new Date(`${dueAt}T09:00:00`).toISOString() : undefined,
      });
      setBody('');
      setNoteType('note');
      setDueAt('');
      await load();
    } catch (err) {
      setError(err?.message || t('notes.saveError'));
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t excluded by design (see load note)
  }, [body, saving, scope, projectId, bundleId, taskId, noteType, dueAt, load]);

  const handleArchive = useCallback(async (id) => {
    const prev = notes;
    setNotes((cur) => cur.filter((n) => n.id !== id));
    try {
      await archiveNote(id);
    } catch (err) {
      setError(err?.message || t('notes.archiveError'));
      setNotes(prev);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t excluded by design (see load note)
  }, [notes]);

  // Reply: ANY authenticated user. Inherits scope/anchor from the parent server-side.
  const handleReply = useCallback(async (parentId, replyBody) => {
    try {
      const result = await createNote({ scope, projectId, bundleId, taskId, parentId, body: replyBody });
      // TICKET-CODEX-3371361574: apiFetch resolves non-GET failures as
      // { success: false, error } instead of throwing. Surface those as
      // thrown errors so NoteThread.sendReply's catch path handles them
      // and does NOT clear/close the reply input on failure.
      if (result && result.success === false) {
        throw new Error(result.error || t('notes.saveError'));
      }
      await load();
    } catch (err) {
      setError(err?.message || t('notes.saveError'));
      throw err; // re-throw so NoteThread.sendReply can keep the input open
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t excluded by design (see load note)
  }, [scope, projectId, bundleId, taskId, load]);

  // Edit: AUTHOR only (server-enforced). Re-fetch to reflect the patched body.
  const handleEdit = useCallback(async (id, newBody) => {
    try {
      await updateNote({ id, body: newBody });
      await load();
    } catch (err) {
      setError(err?.message || t('notes.saveError'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t excluded by design (see load note)
  }, [load]);

  // Change-tag: AUTHOR only, root only. Re-fetch so a decision moves in/out of pinned.
  const handleChangeTag = useCallback(async (id, nextType) => {
    try {
      await updateNote({ id, noteType: nextType, isDecision: nextType === 'decision' });
      await load();
    } catch (err) {
      setError(err?.message || t('notes.saveError'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t excluded by design (see load note)
  }, [load]);

  // TICKET-959 B1: localize note bodies
  const { rows: localizedNotes, translating, error: transError } = useLocalizedRows(
    'notes',
    notes,
    lang,
    { reload: load }
  );

  // Handle edit in shown language
  const handleEditWithLangCheck = useCallback(async (id, newBody) => {
    const note = notes.find(n => n.id === id);
    if (!note) return;
    const shown = shownLanguage(note, ['body'], lang, 'es');
    if (lang !== 'orig' && shown === lang) {
      // Edit in the shown language becomes the source
      await editContent({ table: 'notes', id, lang, edits: { body: newBody } });
      await load();
    } else {
      // Edit in original language
      await handleEdit(id, newBody);
    }
  }, [notes, lang, load, handleEdit]);

  // Split roots from replies; replies nest under their root by parent_id.
  const roots = useMemo(() => localizedNotes.filter((n) => !n.parent_id), [localizedNotes]);
  const repliesByParent = useMemo(() => {
    const map = new Map();
    for (const n of localizedNotes) { // TICKET-959: replies in the content language too
      if (!n.parent_id) continue;
      const arr = map.get(n.parent_id) || [];
      arr.push(n);
      map.set(n.parent_id, arr);
    }
    // Oldest -> newest within each thread.
    for (const arr of map.values()) {
      arr.sort((a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime());
    }
    return map;
  }, [localizedNotes]);

  // Roots ordered newest-first; decisions float to a pinned section.
  const orderedRoots = useMemo(
    () => [...roots].sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()),
    [roots],
  );
  const decisions = useMemo(() => orderedRoots.filter((n) => n.is_decision), [orderedRoots]);
  const dayGroups = useMemo(() => {
    const groups = [];
    for (const n of orderedRoots) {
      if (n.is_decision) continue;
      const label = dayLabel(n.created_at, t, locale);
      const last = groups[groups.length - 1];
      if (last && last.label === label) last.notes.push(n);
      else groups.push({ label, notes: [n] });
    }
    return groups;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t excluded by design (labels re-key on locale)
  }, [orderedRoots, locale]);

  const threadProps = {
    canArchive,
    canEdit,
    onArchive: handleArchive,
    onEdit: handleEditWithLangCheck,
    onChangeTag: handleChangeTag,
    onReply: handleReply,
    t,
  };

  return (
    <div data-testid="notes-tab" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* TICKET-959 B1 — content language toggle */}
      <ContentLangToggle
        data-testid="content-lang-toggle"
        translating={translating}
        error={transError}
        testIdPrefix="notes-content-lang"
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <StickyNote size={16} color="var(--accent)" />
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--t1)' }}>
          {t('notes.title')}
        </span>
        {orderedRoots.length > 0 && (
          <span
            data-testid="notes-count"
            style={{
              fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
              background: 'var(--sprob)', color: 'var(--accent)',
            }}
          >
            {orderedRoots.length}
          </span>
        )}
      </div>

      <div
        data-testid="notes-composer"
        style={{
          background: 'var(--card)', borderRadius: 14,
          border: '1px solid var(--bdrg)', padding: 14, boxShadow: CARD_SHADOW,
          display: 'flex', flexDirection: 'column', gap: 10,
        }}
      >
        <textarea
          data-testid="notes-composer-input"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={t('notes.placeholder')}
          rows={3}
          style={{
            width: '100%', padding: 10, borderRadius: 8,
            border: '1px solid var(--bdr)', background: 'var(--card)', color: 'var(--t1)',
            fontSize: 14, fontFamily: 'inherit', resize: 'vertical',
            outline: 'none', minHeight: 72, lineHeight: 1.5,
          }}
        />
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {NOTE_TYPES.map(({ key, labelKey }) => {
            const active = noteType === key;
            const isDecision = key === 'decision';
            return (
              <button
                key={key}
                data-testid={`notes-type-${key}`}
                onClick={() => setNoteType(key)}
                aria-pressed={active}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  minHeight: 44, padding: '8px 12px', borderRadius: 99,
                  fontSize: 11, fontWeight: active ? 700 : 500,
                  background: active ? 'var(--navy)' : 'var(--glass)',
                  color: active ? 'var(--card)' : 'var(--tm)',
                  border: '1px solid var(--bdr)', cursor: 'pointer',
                  transition: 'all 120ms',
                }}
              >
                {isDecision && <Star size={11} />}
                {t(labelKey)}
              </button>
            );
          })}
          {noteType === 'reminder' && (
            <input
              type="date"
              data-testid="notes-due-at"
              aria-label={t('notes.dueAtPick')}
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
              style={{ minHeight: 44, padding: '8px 10px', borderRadius: 10, border: '1px solid var(--bdr)', background: 'var(--card)', color: 'var(--t1)', fontSize: 13 }}
            />
          )}
          <span style={{ flex: 1 }} />
          <button
            data-testid="notes-composer-save"
            onClick={handleSave}
            disabled={saving || body.trim().length === 0}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              minHeight: 44, padding: '8px 16px', borderRadius: 10,
              fontSize: 13, fontWeight: 600,
              background: 'var(--navy-surface)', color: 'var(--on-navy)', border: 'none',
              cursor: saving || body.trim().length === 0 ? 'default' : 'pointer',
              opacity: saving || body.trim().length === 0 ? 0.5 : 1,
            }}
          >
            <Plus size={14} />
            {saving ? t('notes.saving') : t('notes.save')}
          </button>
        </div>
      </div>

      {error && (
        <div
          data-testid="notes-error"
          style={{
            background: 'var(--bad-tint, rgba(239,68,68,0.1))', color: 'var(--bad)',
            padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 500,
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div
          data-testid="notes-loading"
          style={{ padding: 24, textAlign: 'center', color: 'var(--tm)', fontSize: 13 }}
        >
          {t('notes.loading')}
        </div>
      ) : orderedRoots.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {decisions.length > 0 && (
            <section data-testid="notes-pinned">
              <GroupHeader label={t('notes.pinnedTitle')} count={decisions.length} accent />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {decisions.map((n) => (
                  <NoteThread key={n.id} root={n} replies={repliesByParent.get(n.id) || []} {...threadProps} />
                ))}
              </div>
            </section>
          )}
          {dayGroups.map((g) => (
            <section key={g.label} data-testid={`notes-day-${g.label}`}>
              <GroupHeader label={g.label} count={g.notes.length} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {g.notes.map((n) => (
                  <NoteThread key={n.id} root={n} replies={repliesByParent.get(n.id) || []} {...threadProps} />
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div
          data-testid="notes-empty"
          style={{
            padding: '32px 16px', textAlign: 'center',
            background: 'var(--glass)', borderRadius: 14, border: '1px solid var(--bdrg)',
          }}
        >
          <StickyNote size={24} color="var(--tm)" style={{ marginBottom: 8, opacity: 0.4 }} />
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--t2)' }}>
            {t('notes.empty')}
          </div>
          <div style={{ fontSize: 11, color: 'var(--tm)', marginTop: 4 }}>
            {t('notes.emptyHint')}
          </div>
        </div>
      )}
    </div>
  );
}
